import React, { useCallback } from 'react'

import {
  ImageContainer,
  ImageOverlayContainer,
} from './Common'
import DatesVariant from './Common/DatesVariant'
import SummaryCardImage from './SummaryCardImage'

import HeightFillingImage from 'components/Common/HeightFillingImage'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import offerPageURL from 'lib/offer/offerPageURL'
import TripItemSummary from 'tripPlanner/components/TripModal/TripItems/Summary'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { CruiseItem } from 'tripPlanner/types/tripItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import useOffer from 'hooks/Offers/useOffer'

interface Props {
  item: CruiseItem
}

function CruiseV1Card({ item }: Props) {
  const {
    code,
    startPlace,
    isBooked,
    name,
    type,
    startDate,
    endDate,
    startDay,
    endDay,
  } = item

  const [offer, isLoading] = useOffer<App.Offer | App.Tours.TourV2Offer>(code)
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const openModal = useOpenModal()
  const tripId = useTripId()

  const canEdit = useCanEditTrip()

  const handleClick = useCallback(() => {
    if (isBooked) {
      openModal(TripItemSummary, { item })
    }
    if (canEdit) {
      setImmersiveMode({ tripId, sectionId: tripItemSelectionId(item.id) })
    }
  }, [canEdit, isBooked, item, openModal, setImmersiveMode, tripId])

  if (isLoading) {
    return <LoadingIndicator inline />
  }
  if (!offer) {
    return <>Could not load offer.</>
  }

  const offerUrl = offerPageURL(offer)

  return (
    <>
      {offer?.image?.id && (
        <ImageContainer>
          {offer.image.id && <HeightFillingImage id={offer.image.id} />}
          <ImageOverlayContainer>
            <ProductTypeLabel productType={offer.productType} />
          </ImageOverlayContainer>
        </ImageContainer>
      )}
      {!offer?.image?.id && (
        <SummaryCardImage
          place={startPlace}
          tripId={item.tripId}
          tripItemId={item.id}
        />
      )}

      <DatesVariant
        region={startPlace.region}
        name={name}
        startDate={startDate}
        endDate={endDate}
        type={type}
        isBooked={isBooked}
        onClick={handleClick}
        to={isBooked ? undefined : offerUrl}
        linksToOffer={!isBooked}
        canEdit={canEdit && !isBooked}
        startDay={startDay}
        endDay={endDay}
      />
    </>
  )
}

export default CruiseV1Card
